import { User } from '@/@types/user'
import {
	ADD_TOPPING_SHORTCUT_TOPIC,
	CHANGE_TOPIC_SUBSCRIPTION_STATUS,
	REMOVE_TOPPING_SHORTCUT_TOPIC,
} from '@/constants/api'
import { useMutation } from '@tanstack/react-query'
import { cloneDeep } from 'lodash-es'
import {
	cacheTopic,
	getSubscribedTopicsQueryOptions,
	getTopicsToppingQueryOptions,
	retrieveTopic,
} from '../fetchers/topic'
import { currentUserQueryOptions } from '../fetchers/user'
import queryClient from '../queryClient'
import { post } from '../request'

export function useToppingShortcutTopicToggle(id: string) {
	const { mutateAsync: toggleToppingShortcut } = useMutation({
		mutationFn: async () => {
			const topicData = retrieveTopic(id)
			if (!topicData) {
				return Promise.reject(new Error('No valid topicData'))
			}

			// 根据当前置顶状态决定调用的 API
			return post(
				topicData.inShortcuts ? ADD_TOPPING_SHORTCUT_TOPIC : REMOVE_TOPPING_SHORTCUT_TOPIC,
				{
					id,
				}
			).then((res) => res.data)
		},

		onMutate: async () => {
			const prevTopic = retrieveTopic(id)
			if (!prevTopic) {
				return Promise.reject(new Error('No valid topicData'))
			}

			// 保存修改前的数据状态
			const nextTopic = cloneDeep(prevTopic)

			if (nextTopic) {
				// 更新置顶状态
				nextTopic.inShortcuts = !nextTopic.inShortcuts
				// 更新缓存
				cacheTopic(nextTopic)
			}

			return { prevData: prevTopic }
		},

		onError: (err, _vars, context) => {
			// 发生错误时恢复之前的状态
			if (context?.prevData) {
				cacheTopic(context.prevData)
			}
		},

		onSuccess: () => {
			const user = queryClient.getQueryData<User>(currentUserQueryOptions.queryKey)
			queryClient.invalidateQueries(getTopicsToppingQueryOptions())
			if (user?.username) {
				queryClient.invalidateQueries(getSubscribedTopicsQueryOptions(user.username))
			}
		},
	})

	return {
		toggleToppingShortcut,
	}
}

export function useChangeTopicSubscriptionStatusToggle(id: string) {
	const { mutateAsync: toggleSubscriptionStatus, isPending } = useMutation({
		mutationFn: async () => {
			const topicData = retrieveTopic(id)
			if (!topicData) {
				return Promise.reject(new Error('No valid topicData'))
			}

			const isSubscribed = topicData.subscribedStatusRawValue > 0
			// 根据当前订阅状态决定调用的 API
			return post(CHANGE_TOPIC_SUBSCRIPTION_STATUS, {
				subscribed: isSubscribed,
				topicObjectId: id,
			}).then((res) => res.data)
		},

		onMutate: async () => {
			const prevTopic = retrieveTopic(id)
			if (!prevTopic) {
				return Promise.reject(new Error('No valid topicData'))
			}

			// 保存修改前的数据状态
			const nextTopic = cloneDeep(prevTopic)
			if (nextTopic) {
				const isSubscribed = nextTopic.subscribedStatusRawValue > 0
				// 更新订阅状态
				nextTopic.subscribedStatusRawValue = isSubscribed ? 0 : 1
				// 更新缓存
				cacheTopic(nextTopic)
			}

			return { prevData: prevTopic }
		},

		onError: (err, _vars, context) => {
			// 发生错误时恢复之前的状态
			if (context?.prevData) {
				cacheTopic(context.prevData)
			}
		},

		onSuccess: () => {
			const user = queryClient.getQueryData<User>(currentUserQueryOptions.queryKey)
			queryClient.invalidateQueries(getTopicsToppingQueryOptions())
			if (user?.username) {
				queryClient.invalidateQueries(getSubscribedTopicsQueryOptions(user.username))
			}
		},
	})

	return {
		toggleSubscriptionStatus,
		isPending,
	}
}
